import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Badge,
	Button,
	Empty,
	Modal,
	Popover,
	Spin,
	Typography,
} from "antd";
import { BellOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ko";
import { useNotifications } from "../hooks/useNotifications";
import useIsMobile from "../hooks/useIsMobile";

dayjs.extend(relativeTime);
dayjs.locale("ko");

const { Text } = Typography;

const NotificationBell = () => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const isMobile = useIsMobile();
	const {
		notifications,
		unreadCount,
		loading,
		fetchNotifications,
		markAsRead,
		markAllAsRead,
	} = useNotifications({ enabled: true });

	const handleOpenChange = (nextOpen) => {
		setOpen(nextOpen);
		if (nextOpen) {
			fetchNotifications();
		}
	};

	const handleNotificationClick = async (item) => {
		if (!item.is_read) {
			await markAsRead(item.id);
		}
		setOpen(false);
		if (item.url) {
			navigate(item.url);
		}
	};

	const markAllButton =
		unreadCount > 0 ? (
			<Button
				type="link"
				size="small"
				onClick={markAllAsRead}
				className="notification-bell__mark-all">
				모두 읽음
			</Button>
		) : null;

	const notificationList = (
		<Spin spinning={loading}>
			{notifications.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description="알림이 없습니다"
					className="notification-bell__empty"
				/>
			) : (
				<ul className="notification-bell__list">
					{notifications.map((item) => (
						<li
							key={item.id}
							className={`notification-bell__item${
								item.is_read
									? ""
									: " notification-bell__item--unread"
							}`}
							onClick={() => handleNotificationClick(item)}
							onKeyDown={(event) => {
								if (
									event.key === "Enter" ||
									event.key === " "
								) {
									event.preventDefault();
									handleNotificationClick(item);
								}
							}}
							role="button"
							tabIndex={0}>
							<div className="notification-bell__item-title">
								{item.title}
							</div>
							<div className="notification-bell__item-body">
								{item.body}
							</div>
							<div className="notification-bell__item-time">
								{dayjs(item.created_at).fromNow()}
							</div>
						</li>
					))}
				</ul>
			)}
		</Spin>
	);

	const popoverContent = (
		<div className="notification-bell__panel">
			<div className="notification-bell__panel-header">
				<Text strong>알림</Text>
				{markAllButton}
			</div>
			{notificationList}
		</div>
	);

	const bellButton = (
		<Badge count={unreadCount} size="small" offset={[-2, 2]}>
			<Button
				type="text"
				icon={<BellOutlined />}
				className="notification-bell__button"
				aria-label="알림"
				onClick={
					isMobile
						? () => handleOpenChange(true)
						: undefined
				}
			/>
		</Badge>
	);

	if (isMobile) {
		return (
			<>
				{bellButton}
				<Modal
					title={
						<div className="notification-bell__panel-header">
							<Text strong>알림</Text>
							{markAllButton}
						</div>
					}
					open={open}
					onCancel={() => handleOpenChange(false)}
					footer={null}
					width="95%"
					centered
					className="notification-bell__modal"
					destroyOnClose={false}
					maskClosable
					styles={{
						body: {
							maxHeight: "70vh",
							overflowY: "auto",
							padding: "0 4px 8px",
						},
					}}>
					{notificationList}
				</Modal>
			</>
		);
	}

	return (
		<Popover
			content={popoverContent}
			trigger="click"
			placement="bottomRight"
			open={open}
			onOpenChange={handleOpenChange}
			overlayClassName="notification-bell__popover">
			{bellButton}
		</Popover>
	);
};

export default NotificationBell;
